/**
 * The web mirror of native's per-navigation `openedFromDrawer` flag — iOS AppRouter's route
 * associated value and Android's `fromDrawer` nav-arg. It is the signal `topShowsMenu` reads to
 * decide the leading header affordance: a screen reached FROM the drawer/rail/account cluster
 * shows the MENU (drawer/rail toggle); every other way in — a CTA push, a deep-link, an agent
 * `orion.navigate`, an in-content link — shows BACK. The same route therefore flips based on how
 * it was opened, exactly as on native (which is why this is a per-navigation flag and not a route
 * classification).
 *
 * Web is a URL-based SPA with no nav stack, so the flag is stored PER HISTORY ENTRY, keyed by
 * Next's `history.state.key` — a fresh unique id minted on every `pushState` (next/router
 * router.js: `key: this._key = method !== 'pushState' ? this._key : createKey()`) and preserved
 * across `replaceState`, back/forward and reload. The map lives in `sessionStorage` so it survives
 * a reload of the same tab. (Next's pages router does NOT populate `history.state.idx`, so `key` is
 * the only stable per-entry identity available.)
 *
 * A drawer nav calls `markNavFromDrawer()` synchronously before it pushes; the next
 * `recordNavigation()` (fired on routeChangeComplete / popstate) stamps that intent onto the new
 * entry's key.
 */

// key -> was this entry reached from the drawer? Absent/false = not from drawer (→ BACK).
const STORE_KEY = 'symphony-drawer-nav';
// The tab session's very first entry key — the one `router.back()` would exit the app from.
const FIRST_KEY = 'symphony-nav-first-key';

// Set by a drawer/rail/account/workspace nav, consumed by the next recordNavigation().
let pendingFromDrawer = false;

function stateKey(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const key = window.history.state?.key;
  return typeof key === 'string' ? key : undefined;
}

function session(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

function readMap(): Record<string, boolean> {
  const store = session();
  if (!store) return {};
  try {
    const raw = store.getItem(STORE_KEY);
    return raw ? (JSON.parse(raw) as Record<string, boolean>) : {};
  } catch {
    return {};
  }
}

function writeMap(map: Record<string, boolean>): void {
  const store = session();
  if (!store) return;
  try {
    store.setItem(STORE_KEY, JSON.stringify(map));
  } catch {
    // sessionStorage blocked (private mode / disabled): degrade to always-BACK off home rather
    // than crash. The flag simply won't persist.
  }
}

/** A drawer/rail/account/workspace navigation is about to push — mark the entry it lands on as
 *  drawer-opened. Call synchronously in the click handler, before the `<Link>`/`router.push` runs. */
export function markNavFromDrawer(): void {
  pendingFromDrawer = true;
}

/** Record the entry we've just landed on and return whether it was reached from the drawer. Call on
 *  mount and on every routeChangeComplete / popstate. */
export function recordNavigation(): boolean {
  const key = stateKey();
  if (!key) {
    pendingFromDrawer = false;
    return false;
  }
  const store = session();
  if (store && !store.getItem(FIRST_KEY)) {
    try {
      store.setItem(FIRST_KEY, key);
    } catch {
      /* see writeMap */
    }
  }
  const map = readMap();
  if (!(key in map)) {
    // A brand-new entry (a fresh push): record its source. A revisit — back/forward/reload, whose
    // key is already stored — keeps whatever was recorded when it was first pushed.
    map[key] = pendingFromDrawer;
    writeMap(map);
  } else if (pendingFromDrawer) {
    // Re-navigating from the drawer to an entry already in history (e.g. tapping the rail item for
    // a screen you've visited): honour the fresh drawer intent.
    map[key] = true;
    writeMap(map);
  }
  pendingFromDrawer = false;
  return map[key] === true;
}

/** Whether the current entry was reached from the drawer. `topShowsMenu` treats home as always-MENU
 *  on top of this. */
export function openedFromDrawer(): boolean {
  const key = stateKey();
  return key ? readMap()[key] === true : false;
}

/** Whether going back stays inside the app. False at the tab session's first entry (a deep-link or
 *  direct load), where `router.back()` would leave the site — the caller sends the user home instead. */
export function canGoBackInApp(): boolean {
  const key = stateKey();
  const store = session();
  if (!key || !store) return false;
  const first = store.getItem(FIRST_KEY);
  return first !== null && key !== first;
}
